import {
  DEFAULT_DATE_FORMAT,
  NonBusinessDayFailover,
  Payday,
  PayFrequency,
} from '@aslanpay/portal-common-lib';
import {
  addDays,
  addWeeks,
  addMonths,
  subDays,
  format,
  startOfMonth,
  getDaysInMonth,
  getYear,
  getMonth,
  getDay,
  set,
  subMonths,
  subWeeks,
  isBefore,
  isWeekend,
} from 'date-fns';

export const getMonthsBetween = (startDate: Date, endDate: Date) => {
  const result = [];
  let currentDate = startOfMonth(startDate);

  while (currentDate <= endDate) {
    const monthName = format(currentDate, 'MMM');
    const year = currentDate.getFullYear();
    result.push(`${monthName} ${year % 100}`);
    currentDate = addMonths(currentDate, 1);
  }

  return result;
};

export const calculatePayCycleEndDate = (
  payFrequency: PayFrequency,
  payCycleStartDate: Date
): string => {
  let endDate: Date;

  switch (payFrequency) {
    case 'MONTHLY':
      endDate = addMonths(payCycleStartDate, 1);
      endDate = subDays(endDate, 1);
      return endDate.toString();
    case 'FOUR_WEEKLY':
      endDate = addWeeks(payCycleStartDate, 4);
      endDate = subDays(endDate, 1);
      return endDate.toString();
    case 'FORTNIGHTLY':
      endDate = addWeeks(payCycleStartDate, 2);
      endDate = subDays(endDate, 1);
      return endDate.toString();
    case 'WEEKLY':
      endDate = addWeeks(payCycleStartDate, 1);
      endDate = subDays(endDate, 1);
      return endDate.toString();
    default:
      throw new Error('Invalid pay frequency');
  }
};

export const calculateNinetyDaysFromEndDate = (endDate: Date): string => {
  let ninetyDaysFromEndDate: Date;
  // eslint-disable-next-line prefer-const
  ninetyDaysFromEndDate = addDays(endDate, 90);
  return ninetyDaysFromEndDate.toString();
};

export const getDayValueFromDayString = (day: string): number => {
  const days: string[] = [
    'SUNDAY',
    'MONDAY',
    'TUESDAY',
    'WEDNESDAY',
    'THURSDAY',
    'FRIDAY',
    'SATURDAY',
  ];
  return days.indexOf(day.toUpperCase());
};

export const getFirstPayDate = (
  payday?: Payday,
  payFrequency?: PayFrequency,
  startDate?: string,
  customWeekDay?: string,
  fixedDayValue?: string,
  nDayValue?: number,
  nBusinessDayValue?: number
): Date => {
  let startOfPeriod: Date = new Date('01/01/2025');
  let firstPayDay: Date = new Date('01/01/2025');

  // Set the start date so we can work with it (start of the period date - depending on the frequency type this will changes)
  if (startDate !== undefined) {
    startOfPeriod = new Date(startDate);
  }

  // Now lets work out what the period means against the start date

  switch (payFrequency) {
    case 'MONTHLY':
      // last day of period for monthly is simply the end of the month e.g. 31/01/2025
      // so we get the month of the start date and return the last day of that month NB this doesn't yet take into account ones that don't start on the beginning of the month
      if (payday === 'LAST_DAY_OF_PERIOD') {
        firstPayDay = set(new Date(firstPayDay), {
          year: getYear(startOfPeriod),
          month: getMonth(startOfPeriod),
          date: getDaysInMonth(startOfPeriod),
        });
      }
      // last day of period for monthly is simply the end of the month,
      // adjusting for whether the end of the month is a saturday (-1 day) or a sunday (-2 days)
      if (payday === 'LAST_BUSINESS_DAY') {
        firstPayDay = set(new Date(firstPayDay), {
          year: getYear(startOfPeriod),
          month: getMonth(startOfPeriod),
          date: getDaysInMonth(startOfPeriod),
        });

        // 0 is sunday, 6 is saturday
        if (getDay(firstPayDay) === 0) {
          firstPayDay = subDays(firstPayDay, 2);
        }
        if (getDay(firstPayDay) === 6) {
          firstPayDay = subDays(firstPayDay, 1);
        }
      }
      // the last custom weekday will all depend on two things - which day of the week has been picked
      // and what the last day of the week is e.g. if the last day of the week is a sunday, but the day picked
      // is a monday, then we have to go back 6 days to the monday as the last custom weekday.
      if (payday === 'LAST_CUSTOM_WEEKDAY') {
        let weekdayNumber = 0;
        if (customWeekDay !== undefined) {
          weekdayNumber = getDayValueFromDayString(customWeekDay);
        }

        firstPayDay = set(new Date(firstPayDay), {
          year: getYear(startOfPeriod),
          month: getMonth(startOfPeriod),
          date: getDaysInMonth(startOfPeriod),
        });

        const dayValue: number = getDay(new Date(firstPayDay));

        // 1. if the dayValue matches the required custom weekday
        if (dayValue === weekdayNumber) {
          firstPayDay = new Date(firstPayDay);
        }

        // 2. if the dayValue is > than the required custom weekday value, take days away
        if (dayValue > weekdayNumber) {
          firstPayDay = subDays(new Date(firstPayDay), 7 - dayValue);
        }

        // 3. if the dayValue is < than the required custom weekday value, add days on
        if (dayValue < weekdayNumber) {
          firstPayDay = addDays(new Date(firstPayDay), dayValue - weekdayNumber);
        }
      }

      // if the payday is nth day of the period e.g. 15, then its the 15th day from the start of the period
      if (payday === 'N_DAY_OF_PERIOD') {
        if (nDayValue !== undefined) {
          firstPayDay = addDays(new Date(startOfPeriod), nDayValue);
        }
      }

      // if the payday is nth business day of the period e.g. 11, then it's the 11th business day
      if (payday === 'N_BUSINESS_DAY_OF_PERIOD') {
        if (nBusinessDayValue !== undefined) {
          firstPayDay = addDays(new Date(startOfPeriod), nBusinessDayValue);
        }
      }

      // The easy case is whatever the fixed day is becomes the first pay date
      if (payday === 'FIXED_DAY') {
        if (fixedDayValue !== undefined) {
          firstPayDay = new Date(fixedDayValue);
        }
      }
      break;
    case 'FOUR_WEEKLY':
      firstPayDay = addWeeks(startOfPeriod, 4);
      break;
    case 'FORTNIGHTLY':
      firstPayDay = addWeeks(startOfPeriod, 2);
      break;
    case 'WEEKLY':
      firstPayDay = addWeeks(startOfPeriod, 1);
      break;
    default:
  }

  return firstPayDay;
};

export const calculateMinimumPeriodStartDate = (payFrequency?: PayFrequency): string => {
  let previousPeriodStart = '';
  const today = Date.now();

  switch (payFrequency) {
    case 'MONTHLY':
      previousPeriodStart = subMonths(new Date(today), 1).toDateString();

      return previousPeriodStart;
    case 'FOUR_WEEKLY':
      previousPeriodStart = subWeeks(new Date(today), 4).toDateString();

      return previousPeriodStart;
    case 'FORTNIGHTLY':
      previousPeriodStart = subWeeks(new Date(today), 2).toDateString();

      return previousPeriodStart;
    case 'WEEKLY':
      previousPeriodStart = subWeeks(new Date(today), 1).toDateString();

      return previousPeriodStart;
    default:
      return 'No matching pay frequency';
  }
};

export const isDateBefore = (
  startDate: string,
  calculatedMinimumPeriodStartDate: string
): boolean => {
  let isDtBefore = false;

  if (isBefore(new Date(startDate), new Date(calculatedMinimumPeriodStartDate))) {
    isDtBefore = true;
  }

  return isDtBefore;
};

// Helper function to return the ordinal suffix for a given number.
export function getOrdinalSuffix(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return 'st';
  if (mod10 === 2 && mod100 !== 12) return 'nd';
  if (mod10 === 3 && mod100 !== 13) return 'rd';
  return 'th';
}

/**
 * Render a short "time ago" label (e.g. "3d ago") from an ISO date string.
 */
export function formatTimeAgo(isoDate: string): string {
  const ts = new Date(isoDate).getTime();
  if (Number.isNaN(ts)) return '—';
  const diffMs = Math.max(0, Date.now() - ts);
  const diffS = Math.floor(diffMs / 1000);
  const diffM = Math.floor(diffS / 60);
  const diffH = Math.floor(diffM / 60);
  const diffD = Math.floor(diffH / 24);
  if (diffD > 0) return `${diffD}d ago`;
  if (diffH > 0) return `${diffH}h ago`;
  if (diffM > 0) return `${diffM}m ago`;
  return `${diffS}s ago`;
}

// Helper to compare dates (ignoring time)
function isSameDate(dateLeft: Date, dateRight: Date): boolean {
  return (
    dateLeft.getFullYear() === dateRight.getFullYear() &&
    dateLeft.getMonth() === dateRight.getMonth() &&
    dateLeft.getDate() === dateRight.getDate()
  );
}

// Check if a date is a public holiday (customize as needed)
function isPublicHoliday(date: Date, holidays: Date[]): boolean {
  return holidays.some((holiday) => isSameDate(date, holiday));
}

// Determine if a date is a business day (i.e. not a weekend or public holiday)
function isBusinessDay(date: Date, holidays: Date[]): boolean {
  return !isWeekend(date) && !isPublicHoliday(date, holidays);
}

/**
 * Computes the first pay day based on a start date, calendar type, and payday adjustment action.
 *
 * @param startDate - The starting date.
 * @param calendarType - Determines the period: 'weekly', 'fortnightly', 'four-weekly', or 'monthly'.
 * @param paydayAction - If the computed date is a weekend/public holiday, adjust it:
 *                       'none' returns the date as is,
 *                       'previous' moves to the previous business day,
 *                       'next' moves to the next business day.
 * @param holidays - Optional array of public holiday dates.
 * @returns The computed first pay day as a Date.
 */
export function getFirstPayDay(
  startDate: Date,
  calendarType: PayFrequency,
  paydayAction: NonBusinessDayFailover,
  holidays: Date[] = []
): Date {
  let payDay: Date;

  // Calculate the base pay day by adding one period to the start date.
  switch (calendarType) {
    case 'WEEKLY':
      payDay = addDays(startDate, 7);
      break;
    case 'FORTNIGHTLY':
      payDay = addDays(startDate, 14);
      break;
    case 'FOUR_WEEKLY':
      payDay = addDays(startDate, 28);
      break;
    case 'MONTHLY':
      payDay = addMonths(startDate, 1);
      break;
    default:
      throw new Error('Invalid calendar type');
  }

  // If no adjustment is required, return the computed pay day.
  if (paydayAction === 'NO_CHANGE') {
    return payDay;
  }

  // Adjust the computed pay day if it falls on a weekend or public holiday.
  if (paydayAction === 'NEXT_BUSINESS_DAY') {
    while (!isBusinessDay(payDay, holidays)) {
      payDay = addDays(payDay, 1);
    }
  } else if (paydayAction === 'PREVIOUS_BUSINESS_DAY') {
    while (!isBusinessDay(payDay, holidays)) {
      payDay = subDays(payDay, 1);
    }
  }

  return payDay;
}

// // Example usage:
// const startDate = new Date('2025-01-01');
// const calendarType: CalendarType = 'weekly';
// const paydayAction: PaydayAction = 'next'; // Use 'previous' or 'none' as needed
// const holidays: Date[] = [
//   // Example: new Date('2025-01-08') // Uncomment and add dates as needed
// ];

export const formatDateToDDMMYYYY = (date: string | Date): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return format(dateObj, DEFAULT_DATE_FORMAT);
};

export const formatDateTimeToDDMMYYYYHHMM = (date: string | Date): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return format(dateObj, 'dd/MM/yyyy HH:mm');
};

const dateUtils = {
  getMonthsBetween,
  calculatePayCycleEndDate,
  calculateNinetyDaysFromEndDate,
  getFirstPayDate,
  calculateMinimumPeriodStartDate,
  isDateBefore,
  getOrdinalSuffix,
  getFirstPayDay,
  formatDateToDDMMYYYY,
  formatDateTimeToDDMMYYYYHHMM,
};

export default dateUtils;
